import React, { useState, useEffect } from 'react';

const QuizCard = ({ quiz, onSubmit, savedAnswers = {}, reviewMode = false }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(savedAnswers);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (reviewMode) {
      setAnswers(savedAnswers);
    }
  }, [reviewMode, savedAnswers]);

  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return (
      <div className="card text-center">
        <p className="text-slate-400">No quiz questions available</p>
      </div>
    );
  }

  const handleAnswerSelect = (questionIndex, answer) => {
    if (reviewMode) return; // Prevent changing answers in review mode
    setAnswers({ ...answers, [questionIndex]: answer });
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    let correct = 0;
    quiz.questions.forEach((q, index) => {
      if (answers[index] === q.correctAnswer) {
        correct++;
      }
    });
    const score = Math.round((correct / quiz.questions.length) * 100);
    setShowResults(true);
    onSubmit(answers, score);
  };

  const question = quiz.questions[currentQuestion];
  const isAnswered = answers[currentQuestion] !== undefined;
  const allAnswered = quiz.questions.every((_, index) => answers[index] !== undefined);
  const userAnswer = answers[currentQuestion];
  const correctAnswer = question.correctAnswer;

  if (showResults) {
    const correctCount = quiz.questions.filter((q, i) => answers[i] === q.correctAnswer).length;
    const score = Math.round((correctCount / quiz.questions.length) * 100);

    return (
      <div className="card text-center fade-in scale-in">
        <div className="text-6xl mb-4 bounce-in">
          {score >= 80 ? '🎉' : score >= 60 ? '👏' : '💪'}
        </div>
        <h2 className="text-3xl font-bold mb-4">Quiz Results</h2>
        <div className="text-5xl font-bold gradient-text my-6 pulse">{score}%</div>
        <p className="text-slate-300 mb-2">
          You got {correctCount} out of {quiz.questions.length} questions correct!
        </p>
      </div>
    );
  }

  return (
    <div className="card fade-in-up">
      {/* Review Mode Banner */}
      {reviewMode && (
        <div className="mb-4 p-3 bg-blue-500 bg-opacity-20 border border-blue-500 text-blue-400 rounded-lg text-sm">
          <span className="font-semibold">📝 Review Mode:</span> See your answers and correct solutions
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-slate-400">
            Question {currentQuestion + 1} of {quiz.questions.length}
          </span>
          <span className="text-sm text-slate-400">
            {Object.keys(answers).length} answered
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Question */}
      <div className="mb-6 fade-in" key={currentQuestion}>
        <h3 className="text-xl font-semibold mb-6">{question.question}</h3>

        {/* Options */}
        <div className="space-y-3">
          {question.options.map((option, index) => {
            const isUserAnswer = userAnswer === option;
            const isCorrect = option === correctAnswer;
            const showCorrect = reviewMode && isCorrect;
            const showWrong = reviewMode && isUserAnswer && !isCorrect;

            return (
              <div
                key={index}
                onClick={() => handleAnswerSelect(currentQuestion, option)}
                className={`quiz-option ${
                  answers[currentQuestion] === option ? 'selected' : ''
                } ${showCorrect ? 'border-green-500 bg-green-500 bg-opacity-10' : ''} ${
                  showWrong ? 'border-red-500 bg-red-500 bg-opacity-10' : ''
                } ${reviewMode ? 'cursor-default' : 'hover-lift cursor-pointer'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[currentQuestion] === option
                          ? 'border-indigo-500 bg-indigo-500 scale-in'
                          : 'border-slate-500'
                      } ${showCorrect ? 'border-green-500 bg-green-500' : ''} ${
                        showWrong ? 'border-red-500 bg-red-500' : ''
                      }`}
                    >
                      {answers[currentQuestion] === option && (
                        <div className="w-2 h-2 bg-white rounded-full scale-in"></div>
                      )}
                    </div>
                    <span className="text-slate-200 flex-1">{option}</span>
                  </div>
                  
                  {/* Show correct/wrong indicators in review mode */}
                  {reviewMode && (
                    <>
                      {showCorrect && (
                        <span className="text-green-400 text-sm font-semibold">✓ Correct</span>
                      )}
                      {showWrong && (
                        <span className="text-red-400 text-sm font-semibold">✗ Wrong</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Explanation in review mode */}
        {reviewMode && userAnswer && (
          <div className={`mt-4 p-4 rounded-lg ${
            userAnswer === correctAnswer 
              ? 'bg-green-500 bg-opacity-10 border border-green-500' 
              : 'bg-red-500 bg-opacity-10 border border-red-500'
          }`}>
            <p className={`text-sm ${
              userAnswer === correctAnswer ? 'text-green-400' : 'text-red-400'
            }`}>
              {userAnswer === correctAnswer 
                ? '✓ Your answer is correct!' 
                : `✗ Correct answer: ${correctAnswer}`}
            </p>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center gap-4">
        <button
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          className="btn-outline disabled:opacity-30"
        >
          ← Previous
        </button>

        <div className="flex gap-2">
          {quiz.questions.map((_, index) => {
            const answered = answers[index] !== undefined;
            const isCorrectAnswer = reviewMode && answered && answers[index] === quiz.questions[index].correctAnswer;
            const isWrongAnswer = reviewMode && answered && answers[index] !== quiz.questions[index].correctAnswer;

            return (
              <div
                key={index}
                onClick={() => setCurrentQuestion(index)}
                className={`w-2 h-2 rounded-full cursor-pointer transition-all ${
                  isCorrectAnswer ? 'bg-green-500 scale-125' :
                  isWrongAnswer ? 'bg-red-500 scale-125' :
                  answered ? 'bg-indigo-500 scale-125' :
                  index === currentQuestion ? 'bg-slate-500' : 'bg-slate-700'
                }`}
              ></div>
            );
          })}
        </div>

        {reviewMode ? (
          <button onClick={handlePrevious} className="btn-primary" disabled={currentQuestion === quiz.questions.length - 1}>
            {currentQuestion === quiz.questions.length - 1 ? 'End Review' : 'Next →'}
          </button>
        ) : currentQuestion === quiz.questions.length - 1 ? (
          <button
            onClick={handleSubmit}
            disabled={!allAnswered}
            className="btn-primary disabled:opacity-50"
          >
            Submit Quiz ✓
          </button>
        ) : (
          <button 
            onClick={handleNext} 
            disabled={!isAnswered} 
            className="btn-primary disabled:opacity-50"
          >
            Next →
          </button>
        )}
      </div>
    </div>
  );
};

export default QuizCard;